import React, { useState, useEffect, useCallback } from "react";
import classnames from "classnames";
import { apiClient } from "../../apiClient";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from "reactstrap";

// Configs
import { endpoints } from "../../configs";

// Redux
import { useSelector, useDispatch } from "react-redux";

// Actions
import { fetchList } from "../../actions/table";
import CountBadge from "../../components/base/CountBadge";

import {
  MDF_STATUS_REQUESTED,
  MDF_STATUS_APPROVED,
  TAB_NEW,
  TAB_APPROVED,
  TAB_ALL
} from "./Constants";

// Helper
import {
  getParamsByName,
  isPartner,
  isSuperAdmin,
  isCompanyAdmin,
  isCompanyManager
} from "../../lib/helper";
import { MdfIcon } from "../../assets/img/icons";
// Components
import PageTitle from "../../components/base/PageTitle";
import ReduxTable, { ReduxColumn } from "../../components/base/ReduxTable";
import MoreDropdown from "../../components/base/MoreDropdown";
import Spinner from "../../components/base/Spinner";
import { isBadRequest } from "../../common/http";
import toast from "../../components/base/Toast";
import DeleteModal from "../../components/base/DeleteModal";

const statusStyle = {
  borderRadius: "5px",
  color: "#FFFFFF",
  fontWeight: 600,
  width: "100px",
  fontSize: "12px",
  paddingLeft: "5px",
  paddingRight: "5px"
};

const sortByOptionsList = [
  {
    value: "createdAt:DESC",
    label: "Most Recent"
  },
  {
    value: "request_amount:ASC",
    label: "Amount"
  }
];

const MDF = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTabState] = useState(TAB_NEW);
  const [deleteMdfModal, setDeleteMdfModal] = useState(false);
  const [deleteMdf, setDeleteMdf] = useState(false);

  const reduxTable = useSelector(state => state.table);
  const user = useSelector(state => state.user);
  const roleId = user ? user.roleId : "";

  // Get counts from redux
  const newMdfRequestCount =
    reduxTable[TAB_NEW] && !reduxTable[TAB_NEW].isFetching
      ? reduxTable[TAB_NEW].totalCount
      : 0;
  const approvedMdfRequestCount =
    reduxTable[TAB_APPROVED] && !reduxTable[TAB_APPROVED].isFetching
      ? reduxTable[TAB_APPROVED].totalCount
      : 0;
  const allMdfRequestCount =
    reduxTable[TAB_ALL] && !reduxTable[TAB_ALL].isFetching
      ? reduxTable[TAB_ALL].totalCount
      : 0;

  const setActiveTab = useCallback(() => {
    const status = getParamsByName("section");
    if (status) {
      setActiveTabState(status);
    }
  }, []);

  useEffect(() => {
    if (isPartner(roleId)) {
      _getPartnerDetails();
    }
    setActiveTab();
  }, [roleId, setActiveTab]);

  useEffect(() => {
    setActiveTab();
  }, [location.search, setActiveTab]);

  const _getPartnerDetails = () => {
    setIsLoading(true);
    apiClient
      .get(`${endpoints().partnerAPI}`)
      .then(response => {
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        if (isBadRequest(error)) {
          let errorMessage;
          const errorRequest = error.response.request;
          if (errorRequest && errorRequest.response) {
            errorMessage = JSON.parse(errorRequest.response).message;
          }
          console.error(errorMessage);
        }
      });
  };

  const updateMdfList = useCallback(() => {
    const tab = getParamsByName("section") || getParamsByName("tab");
    const searchItem = getParamsByName("search") || "";

    // Approved List
    dispatch(
      fetchList(TAB_APPROVED, `${endpoints().mdfRequestAPI}/search`, 1, 10, {
        pagination: true,
        status: MDF_STATUS_APPROVED,
        search: tab === "Approved" ? searchItem : ""
      })
    );

    // All List
    dispatch(
      fetchList(TAB_ALL, `${endpoints().mdfRequestAPI}/search`, 1, 10, {
        pagination: true,
        search: tab === "All" ? searchItem : ""
      })
    );

    // New List
    dispatch(
      fetchList(TAB_NEW, `${endpoints().mdfRequestAPI}/search`, 1, 10, {
        pagination: true,
        status: MDF_STATUS_REQUESTED,
        search: tab === "New" ? searchItem : ""
      })
    );
  }, [dispatch]);

  const updateMdfByStatus = async (status, id) => {
    let data = { status };
    return apiClient
      .put(`${endpoints().mdfRequestAPI}/status/${id}`, data)
      .then(response => {
        if (response && response.data) {
          toast.success(response.data.message);
        }
        updateMdfList();
      })
      .catch(error => {
        if (isBadRequest(error)) {
          toast.error(error.response.data.message);
        }
      });
  };

  const handleDelete = id => {
    return apiClient
      .delete(`${endpoints().mdfRequestAPI}/${id}`)
      .then(response => {
        if (response && response.data) {
          toast.success(response.data.message);
        }
        updateMdfList();
      })
      .catch(error => {
        if (isBadRequest(error)) {
          toast.error(error.response.data.message);
        }
      });
  };

  const toggle = tab => {
    setActiveTabState(tab);
    history.push(`/mdf-requests?section=${tab}`);
  };

  const rendermdfTableByStatus = (tableId, params) => {
    const currentUrl = window.location.pathname + window.location.search;
    return (
      <div>
        <ReduxTable
          key={tableId}
          id={tableId}
          apiURL={`${endpoints().mdfRequestAPI}/search`}
          params={params && { ...params }}
          searchPlaceholder={`Search`}
          onRowClick={row => history.push(`/mdf-request/details/${row.id}`)}
          showHeader
          icon={<MdfIcon />}
          message="You can track the mdf requests you have submited here"
          subtextMessage="To get started tap the Submit a MDF button"
          paramsToUrl={true}
          history={history}
          sortByOptions={sortByOptionsList}
          newTableHeading
        >
          <ReduxColumn
            width="200px"
            type="link"
            isClickable="true"
            sortBy="title"
            field="title"
            renderField={row => (
              <Link to={`mdf-request/details/${row.id}`}>{row.title}</Link>
            )}
          >
            MDF Request
          </ReduxColumn>
          <ReduxColumn width="120px" field="partnerCompanyName" disableOnClick>
            Partner
          </ReduxColumn>
          <ReduxColumn
            width="100px"
            field="requestAmount"
            disableOnClick
            renderField={row => (
              <div className="text-left">
                <p>{row.requestAmount ? `$ ${row.requestAmount}` : ""}</p>
              </div>
            )}
          >
            Requested
          </ReduxColumn>
          <ReduxColumn
            width="100px"
            field="status"
            disableOnClick
            renderField={row => (
              <div
                className={`text-center text-uppercase ${
                  row.status !== "Approved"
                    ? row.status === "Declined"
                      ? "bg-danger"
                      : "landing-page-status-draft"
                    : "landing-page-status-active"
                }`}
                style={statusStyle}
              >
                <p>{row.status}</p>
              </div>
            )}
          >
            Status
          </ReduxColumn>
          <ReduxColumn width="110px" field="createdAt" disableOnClick>
            Created At
          </ReduxColumn>
          <ReduxColumn
            width="100px"
            field="Action"
            disableOnClick
            renderField={row => (
              <div className="text-center landing-group-dropdown">
                <MoreDropdown>
                  {row.status === "Closed" && (
                    <DropdownItem
                      onClick={() =>
                        history.push(
                          `/mdf-request/details/${row.id}?redirect=${currentUrl}`
                        )
                      }
                    >
                      View Details
                    </DropdownItem>
                  )}
                  {(isSuperAdmin(roleId) ||
                    isCompanyAdmin(roleId) ||
                    isCompanyManager(roleId)) && (
                    <>
                      {row.status !== "Approved" && (
                        <DropdownItem
                          className="text-success"
                          onClick={() => updateMdfByStatus("Approved", row.id)}
                        >
                          Approve
                        </DropdownItem>
                      )}
                      {row.status !== "Declined" && (
                        <DropdownItem
                          className="text-danger"
                          onClick={() => updateMdfByStatus("Declined", row.id)}
                        >
                          Decline
                        </DropdownItem>
                      )}
                    </>
                  )}
                  <Link
                    className="text-dark text-decoration-none"
                    to={`/mdf-request/details/${row.id}?redirect=${currentUrl}`}
                    style={{ cursor: "pointer" }}
                  >
                    <DropdownItem>Edit</DropdownItem>
                  </Link>
                  {row.status !== "Closed" && (
                    <DropdownItem
                      className="font-weight-bold text-danger"
                      onClick={() => {
                        setDeleteMdfModal(true);
                        setDeleteMdf(row);
                      }}
                    >
                      Delete
                    </DropdownItem>
                  )}
                </MoreDropdown>
              </div>
            )}
          >
            Action
          </ReduxColumn>
        </ReduxTable>
        <DeleteModal
          isOpen={deleteMdfModal}
          toggle={() => setDeleteMdfModal(false)}
          title="Delete MDF"
          label={deleteMdf ? deleteMdf.title : ""}
          deleteFunction={() => handleDelete(deleteMdf.id)}
        />
      </div>
    );
  };

  const searchItem = getParamsByName("search") || "";
  const searchStatus = getParamsByName("section") || activeTab;

  const isNew = searchStatus === TAB_NEW;
  const isApproved = searchStatus === TAB_APPROVED;
  const isAll = searchStatus === TAB_ALL;

  const params = {
    search: searchItem,
    section: searchStatus
  };

  return (
    <div>
      <PageTitle
        label="MDF"
        targetUrl={"/mdf-request/new"}
        buttonLabel="Submit a MDF Request"
      />
      <Nav tabs className="admin-tabs">
        <NavItem>
          <NavLink
            className={classnames({ active: isNew })}
            onClick={() => toggle(TAB_NEW)}
          >
            {TAB_NEW}{" "}
            <CountBadge
              isActive={classnames({ active: isNew })}
              count={newMdfRequestCount}
            />
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: isApproved })}
            onClick={() => toggle(TAB_APPROVED)}
          >
            {TAB_APPROVED}{" "}
            <CountBadge
              isActive={classnames({ active: isApproved })}
              count={approvedMdfRequestCount}
            />
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: isAll })}
            onClick={() => toggle(TAB_ALL)}
          >
            {TAB_ALL}{" "}
            <CountBadge
              isActive={classnames({ active: isAll })}
              count={allMdfRequestCount}
            />
          </NavLink>
        </NavItem>
      </Nav>

      {isLoading && <Spinner />}

      <TabContent activeTab={activeTab}>
        <TabPane tabId={TAB_NEW}>
          {activeTab == TAB_NEW &&
            rendermdfTableByStatus(TAB_NEW, {
              ...params,
              status: MDF_STATUS_REQUESTED,
              tab: TAB_NEW
            })}
        </TabPane>
        <TabPane tabId={TAB_APPROVED}>
          {activeTab == TAB_APPROVED &&
            rendermdfTableByStatus(TAB_APPROVED, {
              ...params,
              status: MDF_STATUS_APPROVED,
              tab: TAB_APPROVED
            })}
        </TabPane>
        <TabPane tabId={TAB_ALL}>
          {activeTab == TAB_ALL &&
            rendermdfTableByStatus(TAB_ALL, { ...params, tab: TAB_ALL })}
        </TabPane>
      </TabContent>
    </div>
  );
};

export default MDF;
